<template>
  <div class="main event-request-form">
    <FormWrapper
        :action="route('event-request.store', { course: course.id })"
        method="POST"
        @submit="formSubmitted"
    >
      <div class="title-bar">
        <div class="row">
          <div class="col-12 col-md-6 mb-4 text-center text-md-left">
            <h1 class="mb-0">Request an Event</h1>
          </div>
          <div
              class="d-none d-sm-block col-12 col-md-6 mb-3 text-center text-md-right"
          >
            <button type="submit" class="btn btn-success mr-2" :disabled="isSubmitting">
              Submit Request
            </button>
            <a class="btn btn-danger" :href="cancelUrl">Cancel &amp; Exit</a>
          </div>
        </div>
      </div>

      <div class="content py-4 px-xl-2">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert"/>
              </transition>
            </div>
            <div class="col-12 mt-5 text-center" v-if="!showForm">
              <a :href="route('catalog.classroom')" class="btn btn-primary">
                Return to Catalog
              </a>
            </div>
          </div>

          <div class="wrap" v-if="showForm">

            <div class="row">
              <div
                  v-if="!!course.event_request_notes"
                  class="col-12 mb-3"
                  v-html="course.event_request_notes"
              ></div>
            </div>

            <div v-if="pastRequests.length > 0" class="row">
              <div class="col-12">
                <h4>Previous Requests</h4>
                <p>Use the buttons provided in the list below to pre-load this form with details from a previous request
                  for this same course.</p>
                <div class="listable">
                  <div class="listable-head">
                    <div class="th">Date</div>
                    <div class="th">Location</div>
                    <div class="th">City/State</div>
                    <div class="th">Sponsor</div>
                    <div class="th">Trainer</div>
                    <div class="th"></div>
                  </div>
                  <div class="listable-data" v-for="request in pastRequests" :key="request.id">
                    <div class="td">{{ request.data.start_date | moment("MM/DD/YYYY") }}</div>
                    <div class="td">{{ request.data.location }}</div>
                    <div class="td">{{ request.data.city }}, {{ request.data.state }}</div>
                    <div class="td">{{ request.sponsor }}</div>
                    <div class="td">{{ request.trainer.first_name }} {{ request.trainer.last_name }}</div>
                    <td class="text-right">
                      <a :href="getRequestCopyLink(request.id)" class="btn btn-primary mr-3">Copy</a>
                    </td>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-12 mb-3 col-xl-6">
                <Field
                    type="text"
                    name="course_placer"
                    label="Course *"
                    :value="course.name"
                    :disabled="true"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="start_date_placer"
                    label="Start Date *"
                    :value="$moment(event.start_date).format('MM/DD/YYYY')"
                    :disabled="true"
                />
                <input
                    type="hidden"
                    name="start_date"
                    :value="$moment(event.start_date).format('YYYY-MM-DD')"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="datepicker"
                    name="end_date"
                    label="End Date *"
                    :value="event.end_date"
                />
              </div>
              <div class="col-12 mb-3 col-md-6">
                <TimezoneField
                    name="timezone"
                    label="Timezone *"
                    :value="event.timezone"
                />
              </div>
              <div class="col-12 mb-3 col-xl-6">
                <EventRequestTimes :value="event.event_times"/>
              </div>
              <div class="col-12 mb-3">
                <Field
                    type="textarea"
                    name="alt_dates"
                    label="Alternative Dates & Times"
                    :value="event.alt_dates"
                    help-text="Specify any alternative dates & times you would prefer in the event that any conflicts arise with the date(s) you have selected."
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="sponsor_placer"
                    label="Sponsor *"
                    :value="sponsorName"
                    :disabled="true"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    v-if="contacts.length > 0"
                    type="select"
                    name="contact_id"
                    label="Local Contact *"
                    :options="contacts"
                    :value="event.contact_id"
                    :hide-select-empty-option="true"
                />
                <template v-else>
                  <Field
                      type="text"
                      name="contact_placer"
                      label="Local Contact *"
                      :value="$root.user.name"
                      :disabled="true"
                  />
                  <input type="hidden" name="contact_id" :value="$root.user.id"/>
                </template>
              </div>
              <div class="col-12 mb-3 col-xl-6">
                <EventRequestTrainer
                    :event="event"
                    :course="course"
                    :trainers="trainers"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">Enrollment</h3>
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="datepicker"
                    name="registration_opens"
                    label="Enrollment Opens *"
                    :value="event.registration_opens"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="datepicker"
                    name="registration_closes"
                    label="Enrollment Closes *"
                    :value="event.registration_closes"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="price"
                    label="Standard Price *"
                    :value="event.price"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="fad_price"
                    label="Factory Authorized Dealer Price *"
                    :value="event.fad_price"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="min_attendees"
                    label="Minimum Attendees *"
                    :value="course.min_attendees"
                    :disabled="true"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="max_attendees"
                    label="Maximum Attendees *"
                    :value="event.max_attendees"
                    :readonly="!course.distributor_billed"
                />
              </div>
              <div class="col-12 mb-3 col-md-6">
                <template v-if="brands.length == 1">
                  <input type="hidden" name="brands[]" :value="brands[0].value"/>
                  <Field
                      type="text"
                      name="brands_placer"
                      label="Brand"
                      :value="brands[0].label"
                      :disabled="true"
                  />
                </template>
                <MultiAssignField
                    v-else
                    type="select"
                    label="Brand"
                    name="brands"
                    :options="brands"
                    :value="event.event_brands"
                />
              </div>
              <div class="d-none d-md-block col-md-6"></div>
              <div class="col-12 mb-3">
                <Field
                    type="checkbox"
                    name="is_private"
                    id="cb_is_private"
                    label="Only people in my distributorship can attend this event"
                    :value="true"
                    :checkbox-value="1"
                />
              </div>
              <div v-if="!!course.managers_only" class="col-12 mb-3">
                <Field
                    type="checkbox"
                    name="managers_only"
                    label="Only distributor and factory employees can attend this event"
                    :value="true"
                    :checkbox-value="1"
                />
              </div>
              <div v-else class="col-12 mb-3">
                <Field
                    type="checkbox"
                    name="allow_dealer_enroll"
                    id="cb_allow_dealer_enroll"
                    label="Allow dealers to enroll themselves"
                    :value="event.allow_dealer_enroll"
                    :checkbox-value="1"
                />
              </div>
              <div class="col-12 mb-3">
                <Field
                    type="checkbox"
                    name="fad_only"
                    id="cb_fad_only"
                    label="Only FAD/Elite Dealers can attend this event"
                    :value="event.fad_only"
                    :checkbox-value="1"
                />
              </div>
              <div v-if="!!course.nate_id" class="col-12 mb-3 col-md-6 col-xl-3">
                <Field
                    type="text"
                    name="nate_id"
                    label="NATE Course ID"
                    :value="course.nate_id"
                    :disabled="true"
                />
              </div>
            </div>

            <div v-if="!course.is_virtual" class="row">
              <div class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">Event Location</h3>
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="location"
                    label="Location / Venue"
                    v-model="location.location"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="address1"
                    label="Address Line 1"
                    v-model="location.address1"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="address2"
                    label="Address Line 2"
                    v-model="location.address2"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="city"
                    label="City"
                    v-model="location.city"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <StateField
                    name="state"
                    label="State/Province"
                    v-model="location.state"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-2">
                <Field
                    type="text"
                    name="zip"
                    label="Zip/Postal Code"
                    v-model="location.zip"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <CountryField
                    name="country"
                    label="Country"
                    v-model="location.country"
                />
              </div>
            </div>

            <div v-if="!course.is_virtual" class="row">
              <div class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">Ship Course Materials To</h3>
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_contact"
                    label="Contact Person"
                    :value="event.shipping_contact"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_phone"
                    label="Phone Number"
                    :value="event.shipping_phone"
                />
              </div>
              <div class="col-12 mb-3">
                <Field
                    type="checkbox"
                    name="use_location_for_shipping"
                    id="cb_use_location_for_shipping"
                    label="The shipping address is the same as the event location"
                    v-model="useLocationForShipping"
                    :checkbox-value="1"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_location"
                    label="Location / Venue"
                    v-model="shipping.location"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_address1"
                    label="Address Line 1"
                    v-model="shipping.address1"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_address2"
                    label="Address Line 2"
                    v-model="shipping.address2"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-4">
                <Field
                    type="text"
                    name="shipping_city"
                    label="City"
                    v-model="shipping.city"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <StateField
                    name="shipping_state"
                    label="State/Province"
                    v-model="shipping.state"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-2">
                <Field
                    type="text"
                    name="shipping_zip"
                    label="Zip/Postal Code"
                    v-model="shipping.zip"
                    :readonly="useLocationForShipping"
                />
              </div>
              <div class="col-12 mb-3 col-md-6 col-xl-3">
                <CountryField
                    name="shipping_country"
                    label="Country"
                    v-model="shipping.country"
                    :readonly="useLocationForShipping"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">Notes</h3>
              </div>
              <div class="col-12 mb-3">
                <Field
                    type="textarea"
                    name="notes"
                    label="Event Notes"
                    :value="event.notes"
                    help-text="Enter any information in the field above that attendees of this event should be aware of, including dealer-specific cancellation policies."
                />
              </div>
              <div class="col-12 mb-3">
                <Field
                    type="textarea"
                    name="admin_notes"
                    label="Admin Notes"
                    :value="event.admin_notes"
                    help-text="Enter any additional information in the field above that you would like to convey to the My Learning Center administrator regarding this request."
                />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">File Attachments</h3>
                <p class="font-size-sm">
                  Use the options below to attach one or more image or document
                  files to this event. The files will be available for download
                  from the event listing. If you do not want dealers to have
                  access to the file please check the box below.
                </p>
              </div>
              <div class="col-12">
                <EventRequestDownloads
                    :course="course"
                    :downloads="event.event_downloads"
                />
              </div>
            </div>

            <div class="row mb-5">
              <div v-if="!!course.cancel_policy" class="col-12">
                <hr/>
                <h3 class="mb-4 text-uppercase">Cancellation Policy</h3>
                <div
                    class="font-size-sm mb-4"
                    v-html="course.cancel_policy"
                ></div>
                <textarea
                    name="cancel_policy"
                    :value="course.cancel_policy"
                    class="d-none"
                />
                <Field
                    type="checkbox"
                    name="agree_to_terms"
                    id="cb_agree_to_terms"
                    label="I have read and agree to the terms of the cancellation policy"
                    :value="event.agree_to_terms"
                    :checkbox-value="1"
                />
              </div>
              <input v-else type="hidden" name="agree_to_terms" value="1"/>
            </div>

            <div class="row">
              <div class="col-12 text-center text-lg-left">
                <button
                    type="submit"
                    class="btn btn-success w-75 mb-3 w-sm-auto mr-sm-2 mb-sm-0"
                    :disabled="isSubmitting"
                >
                  Submit Request
                </button>
                <a class="btn btn-danger w-75 w-sm-auto" :href="cancelUrl"
                >Cancel &amp; Exit</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import Uploader from "components/pages/admin/assets/partials/Uploader";
import EventRequestTimes from "./partials/EventRequestTimes";
import EventRequestTrainer from "./partials/EventRequestTrainer";
import EventRequestDownloads from "./partials/EventRequestDownloads";

export default {
  mixins: [DisplaysAlerts],

  components: {
    Uploader,
    EventRequestTimes,
    EventRequestTrainer,
    EventRequestDownloads,
  },

  props: {
    event: {type: Object, required: true},
    course: {type: Object, required: true},
    brands: {type: Array, required: true},
    sponsor: {
      type: Object, default: () => {
      }
    },
    trainers: {type: Array, default: () => []},
    contacts: {type: Array, default: () => []},
    pastRequests: {type: Array, default: () => []},
    referer: {type: String, default: null},
  },

  data() {
    return {
      isSubmitting: false,
      location: {
        location: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        country: null,
      },
      shipping: {
        location: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        country: null,
      },
      showForm: true,
      useLocationForShipping: false,
    };
  },

  computed: {
    cancelUrl() {
      return this.referer || this.route("catalog.classroom");
    },
    pastRequestColumns() {
      return [
        {key: "date", label: "Date"},
        {key: "location", label: "Location"},
        {key: "city", label: "City"},
        {key: "sponsor", label: "Sponsor"},
        {key: "trainer", label: "Trainer"},
      ];
    },
    sponsorName() {
      return !_.isEmpty(this.sponsor) ? this.sponsor.name : "Factory Sponsored";
    },
  },

  methods: {
    getRequestCopyLink(previous_request_id) {
      let route = this.route('event-request.create', {
        course: this.course.id
      });

      route += '?start_date=' + this.$moment(this.event.start_date).format('YYYY-MM-DD');
      route += '&event_request_id=' + previous_request_id;

      return route;
    },
    formSubmitted() {
      if (this.isSubmitting) return;

      this.isSubmitting = true;
    }
  },

  watch: {
    location: {
      handler: function (val) {
        if (this.useLocationForShipping) {
          this.shipping = _.cloneDeep(this.location);
        }
      },
      deep: true,
    },
    useLocationForShipping(val) {
      if (!!this.useLocationForShipping) {
        this.shipping = _.cloneDeep(this.location);
      }
    },
  },

  created() {
    if (this.successMessage) {
      this.showForm = false;
    }
    this.location.location = this.event.location || null;
    this.location.address1 = this.event.address1 || null;
    this.location.address2 = this.event.address2 || null;
    this.location.city = this.event.city || null;
    this.location.state = this.event.state || null;
    this.location.zip = this.event.zip || null;
    this.location.country = this.event.country || "US";
    this.shipping.location = this.event.shipping_location || null;
    this.shipping.address1 = this.event.shipping_address1 || null;
    this.shipping.address2 = this.event.shipping_address2 || null;
    this.shipping.city = this.event.shipping_city || null;
    this.shipping.state = this.event.shipping_state || null;
    this.shipping.zip = this.event.shipping_zip || null;
    this.shipping.country = this.event.shipping_country || "US";
    this.useLocationForShipping = !!this.event.use_location_for_shipping;
  },
};
</script>